import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { DepositStatus } from '@repo/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { useDeposit, useSaveDeposit } from '../../hooks/usePlatformConfig';
import { formatDateTime, formatVnd } from '../../lib/adminFormat';

/** Ký quỹ (deposit) tab: this is a rough estimate derived from IPN counts ×
 *  an assumed per-transaction cost — SePay doesn't expose a live balance API,
 *  so an admin periodically re-checkpoints it against SePay's monthly email
 *  and the "Điều chỉnh" dialog. Never treat this figure as authoritative. */
export function BankhubDepositPanel() {
  const { data, isLoading, error } = useDeposit();
  const [adjustOpen, setAdjustOpen] = useState(false);

  if (isLoading) {
    return <p className="text-muted-foreground">Đang tải...</p>;
  }

  if (error || !data) {
    return <p className="text-destructive">Không thể tải trạng thái ký quỹ. Vui lòng thử lại sau.</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        Số liệu dưới đây là <strong>ước tính</strong>, tính từ số giao dịch IPN kể từ mốc kiểm tra
        gần nhất nhân với chi phí/giao dịch giả định, không phải số dư thật của BankHub. Đối chiếu
        với email báo cáo hàng tháng của SePay rồi cập nhật lại mốc bằng nút "Điều chỉnh".
      </p>

      {data.isLow && (
        <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <p className="font-medium">Số dư ký quỹ ước tính đang thấp</p>
            <p className="mt-0.5">
              Còn lại ước tính {formatVnd(data.remainingVnd)}, dưới ngưỡng cảnh báo{' '}
              {formatVnd(data.lowThresholdVnd)}. Nên nạp thêm ký quỹ ở SePay sớm.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-surface-600">Đã tiêu tháng này</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-surface-900">
              {formatVnd(data.spentThisMonthVnd)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{data.ipnThisMonth} giao dịch IPN</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-surface-600">Đã tiêu từ mốc</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-surface-900">
              {formatVnd(data.spentSinceCheckpointVnd)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.ipnSinceCheckpoint} giao dịch IPN kể từ {formatDateTime(data.balanceAsOf)}
            </p>
          </CardContent>
        </Card>
        <Card className={data.isLow ? 'border-destructive/40' : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-surface-600">Còn lại ước tính</CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={
                'text-2xl font-semibold tabular-nums ' +
                (data.isLow ? 'text-destructive' : 'text-surface-900')
              }
            >
              {formatVnd(data.remainingVnd)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Ngưỡng cảnh báo {formatVnd(data.lowThresholdVnd)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div>
        <Button type="button" variant="outline" onClick={() => setAdjustOpen(true)}>
          Điều chỉnh
        </Button>
      </div>

      <AdjustDepositDialog open={adjustOpen} onOpenChange={setAdjustOpen} current={data} />
    </div>
  );
}

function toDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AdjustDepositDialog({
  open,
  onOpenChange,
  current,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: DepositStatus;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open && <AdjustDepositForm current={current} onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function AdjustDepositForm({ current, onDone }: { current: DepositStatus; onDone: () => void }) {
  const saveDeposit = useSaveDeposit();
  const [balanceVnd, setBalanceVnd] = useState(String(current.balanceVnd));
  const [balanceAsOf, setBalanceAsOf] = useState(toDateTimeLocal(current.balanceAsOf));
  const [perTxnCostVnd, setPerTxnCostVnd] = useState(String(current.perTxnCostVnd));
  const [lowThresholdVnd, setLowThresholdVnd] = useState(String(current.lowThresholdVnd));

  const parsed = {
    balanceVnd: Number(balanceVnd),
    perTxnCostVnd: Number(perTxnCostVnd),
    lowThresholdVnd: Number(lowThresholdVnd),
  };
  const valid =
    balanceAsOf.length > 0 &&
    Number.isFinite(parsed.balanceVnd) &&
    parsed.balanceVnd >= 0 &&
    Number.isFinite(parsed.perTxnCostVnd) &&
    parsed.perTxnCostVnd >= 1 &&
    Number.isFinite(parsed.lowThresholdVnd) &&
    parsed.lowThresholdVnd >= 0;
  const disabled = saveDeposit.isPending || !valid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    saveDeposit.mutate(
      {
        balanceVnd: parsed.balanceVnd,
        balanceAsOf: new Date(balanceAsOf).toISOString(),
        perTxnCostVnd: parsed.perTxnCostVnd,
        lowThresholdVnd: parsed.lowThresholdVnd,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Điều chỉnh ký quỹ</DialogTitle>
        <DialogDescription>
          Nhập lại số dư và mốc thời gian theo email đối chiếu của SePay. Chi phí/giao dịch và
          ngưỡng cảnh báo dùng để tính số liệu ước tính ở trên.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-1.5">
          <Label htmlFor="deposit-balance">Số dư (đ)</Label>
          <Input
            id="deposit-balance"
            type="number"
            min={0}
            value={balanceVnd}
            onChange={(e) => setBalanceVnd(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="deposit-as-of">Mốc thời gian</Label>
          <input
            id="deposit-as-of"
            type="datetime-local"
            value={balanceAsOf}
            onChange={(e) => setBalanceAsOf(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="deposit-per-txn">Chi phí/giao dịch (đ)</Label>
          <Input
            id="deposit-per-txn"
            type="number"
            min={1}
            value={perTxnCostVnd}
            onChange={(e) => setPerTxnCostVnd(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="deposit-threshold">Ngưỡng cảnh báo (đ)</Label>
          <Input
            id="deposit-threshold"
            type="number"
            min={0}
            value={lowThresholdVnd}
            onChange={(e) => setLowThresholdVnd(e.target.value)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={disabled}>
          {saveDeposit.isPending ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </DialogFooter>
    </form>
  );
}
